/** Parse deep-link query params; recover when a link dropped `&` before `tab=` (e.g. `1211130-1tab=4`). */
export const parseTenantDeepLink = (
  serviceAccountNumberRaw: string | null,
  tabRaw: string | null
): { serviceAccountNumber?: string; tab?: number } => {
  let serviceAccountNumber = serviceAccountNumberRaw?.trim() || undefined
  let tabFromMalformed: number | undefined

  if (serviceAccountNumber != null) {
    const malformed = serviceAccountNumber.match(/^(.*?)tab=(\d+)$/i)
    if (malformed != null) {
      const stripped = malformed[1]?.trim()
      if (stripped) serviceAccountNumber = stripped
      const parsedEmbedded = Number.parseInt(malformed[2] ?? '', 10)
      if (Number.isFinite(parsedEmbedded) && parsedEmbedded >= 0) tabFromMalformed = parsedEmbedded
    }
  }

  const parsedTab = tabRaw != null ? Number.parseInt(tabRaw, 10) : Number.NaN
  const tabFromQuery = Number.isFinite(parsedTab) && parsedTab >= 0 ? parsedTab : undefined

  return {
    serviceAccountNumber,
    // Prefer an explicit `tab` query param; fall back to a tab recovered from a malformed SAN.
    tab: tabFromQuery ?? tabFromMalformed,
  }
}

/**
 * A `?serviceAccountNumber=` deep link stops applying once the operator opens a
 * different account, collapses any account, or the param matches no account.
 * Leaving it on the URL re-opens and re-highlights the searched account on the
 * next tenant refresh (WEB-7239). Same-landing scroll is already one-shot.
 */
export const shouldClearDeepLinkOnAccountOpen = (
  deepLinkAccountId: string | undefined,
  openedAccountId: string,
  open: boolean,
  hasDeepLinkParam = deepLinkAccountId != null
): boolean => {
  if (!hasDeepLinkParam) return false
  if (!open) return true
  return deepLinkAccountId == null || openedAccountId !== deepLinkAccountId
}

/** Drop `serviceAccountNumber` only. `tab` stays — it is already seeded on the opened account. */
export const retireTenantDeepLinkSearchParams = (prev: URLSearchParams): URLSearchParams => {
  const next = new URLSearchParams(prev)
  next.delete('serviceAccountNumber')
  return next
}
